<script setup lang="ts">
  import Badge from '../ui/badge/Badge.vue'
  import { useRouter } from 'vue-router'
  import { computed, inject, ref, type Ref } from 'vue'
  import type {
    ClientCase,
    ClientQuestionnaireSubmission,
    ClientRequest,
    ClientPDFFormSubmission,
    OnboardingPackageSessionDetail,
    CustomerQuestionnaire,
    CustomerForm,
    PDFFormTemplate
  } from '@/types/generated'
  import { OnboardingSessionsService } from '@/types/generated'
  import ClientPortalCompleteButton from '@/components/clientPortal/clientPortalCompleteButton.vue'
  import ClientPortalDownloadButton from '@/components/clientPortal/clientPortalDownloadButton.vue'
  import { CalendarIcon, ListTodo, FileText, CheckCircle2, Clock } from 'lucide-vue-next'
  import { ROUTES } from '@/router/routes'
  import { useTranslation } from '@/composables/useTranslation'
  import { useThemeColor } from '@/composables/useThemeColorV3'
  import { genericErrorToast } from '@/lib/toasts'

  const router = useRouter()
  const caseRecord = inject<Ref<ClientCase | null>>('caseRecord')
  const onboardingSession = inject<Ref<OnboardingPackageSessionDetail | null>>('onboardingSession')
  const accessToken = inject<Ref<string | null>>('accessToken')
  const sessionToken = inject<Ref<string | null>>('sessionToken')
  const refreshData = inject<() => Promise<void>>('refreshData')

  const isOnboardingSession = computed(() => !!onboardingSession?.value)
  const startingItemKey = ref<string | null>(null)
  const themeColor = computed(() => useThemeColor().value)
  const { t } = useTranslation({
    en: {
      dueBy: 'Due by',
      questionsAnswered: 'questions answered',
      completed: 'Completed',
      pending: 'Pending',
      pleaseFillOutDocument: 'Please fill out the document',
      completeItem: 'Complete {title}',
      downloadItem: 'Download {title}',
      viewItem: 'View {title}',
      questionnaire: 'Questionnaire',
      document: 'Document',
      statusLabel: 'Status: {status}',
      progressLabel: '{answered} of {total} questions answered for {title}',
      requiredActions: 'Required actions'
    },
    de: {
      dueBy: 'Fällig bis',
      questionsAnswered: 'Fragen beantwortet',
      completed: 'Abgeschlossen',
      pending: 'Ausstehend',
      pleaseFillOutDocument: 'Bitte füllen Sie das Dokument aus',
      completeItem: '{title} bearbeiten',
      downloadItem: '{title} herunterladen',
      viewItem: '{title} anzeigen',
      questionnaire: 'Fragebogen',
      document: 'Dokument',
      statusLabel: 'Status: {status}',
      progressLabel: '{answered} von {total} Fragen beantwortet für {title}',
      requiredActions: 'Erforderliche Aktionen'
    }
  })

  const formatDate = (dateString: string | number | Date) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date)
  }

  // Discriminated union - items are tagged at the source, no runtime type checking needed
  type TaggedItem =
    | { type: 'questionnaire_submission'; data: ClientQuestionnaireSubmission }
    | { type: 'form_request'; data: ClientRequest }
    | { type: 'pdf_form_submission'; data: ClientPDFFormSubmission }
    | { type: 'unstarted_questionnaire'; data: CustomerQuestionnaire }
    | { type: 'unstarted_form'; data: CustomerForm }
    | { type: 'unstarted_pdf_template'; data: PDFFormTemplate }

  function hasResponse(request: ClientRequest): boolean {
    return request.response_present as unknown as boolean
  }

  function isUnstartedType(item: TaggedItem): boolean {
    return (
      item.type === 'unstarted_questionnaire' ||
      item.type === 'unstarted_form' ||
      item.type === 'unstarted_pdf_template'
    )
  }

  function isQuestionnaireType(item: TaggedItem): boolean {
    return item.type === 'questionnaire_submission' || item.type === 'unstarted_questionnaire'
  }

  function isSubmitted(item: TaggedItem): boolean {
    switch (item.type) {
      case 'questionnaire_submission':
        return item.data.state === 'CLOSED'
      case 'pdf_form_submission':
        return item.data.submission_status === 'completed'
      case 'form_request':
        return hasResponse(item.data)
      default:
        return false
    }
  }

  function getTitle(item: TaggedItem): string {
    switch (item.type) {
      case 'questionnaire_submission':
        return item.data.title
      case 'pdf_form_submission':
        return item.data.template_name
      case 'form_request':
        return item.data.form?.title || item.data.form?.document_name || ''
      case 'unstarted_questionnaire':
        return item.data.title
      case 'unstarted_form':
        return item.data.title || item.data.document_name || ''
      case 'unstarted_pdf_template':
        return item.data.name
    }
  }

  function getDueDate(item: TaggedItem): string | number | Date {
    switch (item.type) {
      case 'questionnaire_submission':
        return item.data.state === 'CLOSED' ? '' : item.data.due_at
      case 'pdf_form_submission':
        return item.data.submission_status === 'completed' ? '' : item.data.due_at || ''
      case 'form_request':
        return hasResponse(item.data) ? '' : item.data.form.due_at
      default:
        return ''
    }
  }

  function getProgressText(item: TaggedItem, showFullText: boolean = true): string {
    if (isUnstartedType(item)) {
      return t('pleaseFillOutDocument')
    }
    switch (item.type) {
      case 'questionnaire_submission': {
        if (item.data.state === 'CLOSED') return ''
        const numberPart = `${item.data.answered_question_count}/${item.data.question_count}`
        return showFullText ? `${numberPart} ${t('questionsAnswered')}` : numberPart
      }
      case 'pdf_form_submission':
        return item.data.submission_status === 'completed' ? '' : t('pleaseFillOutDocument')
      case 'form_request':
        return hasResponse(item.data) ? '' : t('pleaseFillOutDocument')
      default:
        return ''
    }
  }

  function getProgressPercentage(item: TaggedItem): number {
    switch (item.type) {
      case 'questionnaire_submission':
        return Math.round((item.data.answered_question_count / item.data.question_count) * 100)
      case 'pdf_form_submission':
        return item.data.progress_percentage || 0
      case 'form_request':
        return hasResponse(item.data) ? 100 : 0
      default:
        return 0
    }
  }

  function getBadgeText(item: TaggedItem): string {
    if (isUnstartedType(item)) return t('pending')
    switch (item.type) {
      case 'questionnaire_submission':
        return item.data.state === 'CLOSED' ? t('completed') : t('pending')
      case 'pdf_form_submission':
        return item.data.submission_status === 'completed' ? t('completed') : t('pending')
      case 'form_request':
        return hasResponse(item.data) ? t('completed') : t('pending')
      default:
        return t('pending')
    }
  }

  function getBadgeClass(item: TaggedItem): string {
    const pendingClass = `bg-${themeColor.value}-100 text-${themeColor.value}-700`
    const completedClass = 'bg-teal-100 text-teal-700'

    if (isUnstartedType(item)) return pendingClass
    switch (item.type) {
      case 'questionnaire_submission':
        return item.data.state === 'CLOSED' ? completedClass : pendingClass
      case 'pdf_form_submission':
        return item.data.submission_status === 'completed' ? completedClass : pendingClass
      case 'form_request':
        return hasResponse(item.data) ? completedClass : pendingClass
      default:
        return pendingClass
    }
  }

  function getStatusPriority(item: TaggedItem): number {
    if (isUnstartedType(item)) return 0
    switch (item.type) {
      case 'questionnaire_submission':
        switch (item.data.state) {
          case 'RECEIVED':
            return 1
          case 'OPEN':
            return 2
          case 'CLOSED':
            return 3
          default:
            return 4
        }
      case 'pdf_form_submission':
        return item.data.submission_status === 'completed' ? 3 : 1
      case 'form_request':
        return hasResponse(item.data) ? 3 : 1
      default:
        return 4
    }
  }

  const combinedItems = computed((): TaggedItem[] => {
    const items: TaggedItem[] = []

    if (isOnboardingSession.value && onboardingSession?.value) {
      const session = onboardingSession.value

      // Tag unstarted items (templates) - we KNOW the type from the source array
      ;(session.questionnaires || []).forEach((q) =>
        items.push({ type: 'unstarted_questionnaire', data: q })
      )
      ;(session.forms || []).forEach((f) => items.push({ type: 'unstarted_form', data: f }))
      ;(session.pdf_form_templates || []).forEach((t) =>
        items.push({ type: 'unstarted_pdf_template', data: t })
      )

      // Tag started items (submissions)
      ;(session.case?.questionnaire_submissions || [])
        .filter((s) => !s.is_third_party_questionnaire)
        .forEach((s) => items.push({ type: 'questionnaire_submission', data: s }))
      ;(session.form_requests || [])
        .filter((r) => !r.is_third_party)
        .forEach((r) => items.push({ type: 'form_request', data: r }))
      ;(session.pdf_form_submissions || [])
        .filter((s) => !s.is_external)
        .forEach((s) => items.push({ type: 'pdf_form_submission', data: s }))
    } else {
      // Regular case - use case record data
      ;(caseRecord?.value?.questionnaire_submissions || [])
        .filter((s) => !s.is_third_party_questionnaire)
        .forEach((s) => items.push({ type: 'questionnaire_submission', data: s }))
      ;(caseRecord?.value?.form_requests || [])
        .filter((r) => !r.is_third_party)
        .forEach((r) => items.push({ type: 'form_request', data: r }))
      ;(caseRecord?.value?.pdf_form_submissions || [])
        .filter((s) => !s.is_external)
        .forEach((s) => items.push({ type: 'pdf_form_submission', data: s }))
    }

    return items.sort((a, b) => getStatusPriority(a) - getStatusPriority(b))
  })

  // Build query params - only include tokens that have values
  function buildQueryParams(): Record<string, string> {
    const params: Record<string, string> = {}
    if (sessionToken?.value) {
      params.session_token = sessionToken.value
    } else if (accessToken?.value) {
      params.access_token = accessToken.value
    }
    return params
  }

  function navigateToQuestionaire(submissionId: string) {
    router.push({
      path: `${ROUTES.CLIENT_PORTAL.QUESTIONNAIRE_DETAIL.replace(':uuid', '')}${submissionId}`,
      query: buildQueryParams()
    })
  }

  function navigateToDocument(token: string) {
    router.push({
      path: `${ROUTES.CLIENT_PORTAL.PDF_TEMPLATE_DETAIL.replace(':form_token', '')}${token}`,
      query: buildQueryParams()
    })
  }

  function navigateToPDFFormSubmission(submissionUuid: string) {
    router.push({
      path: `/client/portal/pdf-form-submissions/${submissionUuid}`,
      query: buildQueryParams()
    })
  }

  async function startQuestionnaire(questionnaire: CustomerQuestionnaire) {
    if (!onboardingSession?.value?.uuid || startingItemKey.value) return

    startingItemKey.value = `uq-${questionnaire.uuid}`
    try {
      const response = await OnboardingSessionsService.onboardingSessionsStartQuestionnaireCreate({
        uuid: onboardingSession.value.uuid,
        requestBody: { questionnaire_uuid: questionnaire.uuid }
      })

      if (response?.short_id) {
        navigateToQuestionaire(response.short_id)
      } else {
        await refreshData?.()
      }
    } catch (error) {
      genericErrorToast()
    } finally {
      startingItemKey.value = null
    }
  }

  async function startForm(form: CustomerForm) {
    if (!onboardingSession?.value?.uuid || startingItemKey.value) return

    startingItemKey.value = `uf-${form.id}`
    try {
      const response = await OnboardingSessionsService.onboardingSessionsStartFormCreate({
        uuid: onboardingSession.value.uuid,
        requestBody: { form_id: form.id }
      })

      if (response && 'token' in response && response.token) {
        navigateToDocument(response.token)
      } else {
        await refreshData?.()
      }
    } catch (error) {
      genericErrorToast()
    } finally {
      startingItemKey.value = null
    }
  }

  async function startPDFFormTemplate(template: PDFFormTemplate) {
    if (!onboardingSession?.value?.uuid || startingItemKey.value) return

    startingItemKey.value = `upft-${template.uuid}`
    try {
      const response = await OnboardingSessionsService.onboardingSessionsStartFormCreate({
        uuid: onboardingSession.value.uuid,
        requestBody: { pdf_form_template_uuid: template.uuid }
      })

      if (response && 'uuid' in response) {
        navigateToPDFFormSubmission(response.uuid)
      } else {
        await refreshData?.()
      }
    } catch (error) {
      genericErrorToast()
    } finally {
      startingItemKey.value = null
    }
  }

  function getItemKey(item: TaggedItem): string {
    switch (item.type) {
      case 'questionnaire_submission':
        return `qs-${item.data.uuid}`
      case 'pdf_form_submission':
        return `pfs-${item.data.uuid}`
      case 'form_request':
        return `fr-${item.data.id}`
      case 'unstarted_questionnaire':
        return `uq-${item.data.uuid}`
      case 'unstarted_form':
        return `uf-${item.data.id}`
      case 'unstarted_pdf_template':
        return `upft-${item.data.uuid}`
    }
  }

  function getDataCy(item: TaggedItem): string {
    return isQuestionnaireType(item)
      ? 'ClientPortalItemQuestionnaire'
      : 'ClientPortalItemPdfTemplate'
  }

  const buttonType = (item: TaggedItem) => {
    const itemKey = getItemKey(item)
    const isThisItemLoading = startingItemKey.value === itemKey
    const isAnyItemLoading = !!startingItemKey.value

    switch (item.type) {
      case 'unstarted_questionnaire':
        return {
          component: ClientPortalCompleteButton,
          onClick: () => startQuestionnaire(item.data),
          props: { loading: isThisItemLoading, disabled: isAnyItemLoading && !isThisItemLoading }
        }
      case 'unstarted_form':
        return {
          component: ClientPortalCompleteButton,
          onClick: () => startForm(item.data),
          props: { loading: isThisItemLoading, disabled: isAnyItemLoading && !isThisItemLoading }
        }
      case 'unstarted_pdf_template':
        return {
          component: ClientPortalCompleteButton,
          onClick: () => startPDFFormTemplate(item.data),
          props: { loading: isThisItemLoading, disabled: isAnyItemLoading && !isThisItemLoading }
        }
      case 'pdf_form_submission':
        if (item.data.submission_status === 'completed') {
          return {
            component: ClientPortalDownloadButton,
            props: {
              pdfUrl: item.data.flattened_pdf || item.data.processed_pdf || '',
              filename: item.data.template_name || 'document.pdf',
              documentUuid: item.data.document_uuid
            }
          }
        }
        return {
          component: ClientPortalCompleteButton,
          onClick: () => navigateToPDFFormSubmission(item.data.uuid)
        }
      case 'form_request':
        if (hasResponse(item.data)) {
          return {
            component: ClientPortalDownloadButton,
            props: {
              pdfUrl: item.data.form.file || '',
              filename: item.data.form.document_name || 'document.pdf',
              requestToken: item.data.token
            }
          }
        }
        return {
          component: ClientPortalCompleteButton,
          onClick: () => navigateToDocument(item.data.token)
        }
      case 'questionnaire_submission':
        if (item.data.state === 'CLOSED') {
          return {
            component: ClientPortalDownloadButton,
            props: {
              pdfUrl: item.data.pdf_url || '',
              filename: `${item.data.title}.pdf`,
              documentUuid: item.data.uuid
            }
          }
        }
        return {
          component: ClientPortalCompleteButton,
          onClick: () => navigateToQuestionaire(item.data.short_id)
        }
    }
  }

  function showProgressBar(item: TaggedItem): boolean {
    return item.type === 'questionnaire_submission' && item.data.state !== 'CLOSED'
  }

  function getAccessibleButtonName(item: TaggedItem): string {
    const title = getTitle(item)
    if (isSubmitted(item)) {
      return t('downloadItem', { title })
    }
    return t('completeItem', { title })
  }

  function getProgressAriaLabel(item: TaggedItem): string | undefined {
    if (item.type !== 'questionnaire_submission' || item.data.state === 'CLOSED') return undefined
    return t('progressLabel', {
      answered: item.data.answered_question_count.toString(),
      total: item.data.question_count.toString(),
      title: getTitle(item)
    })
  }
</script>

<template>
  <ul
    data-cy="ClientPortalItemsV3"
    class="space-y-4"
    role="list"
    :aria-label="t('requiredActions')"
  >
    <li
      v-for="item in combinedItems"
      :key="getItemKey(item)"
      :data-cy="getDataCy(item)"
      class="rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
    >
      <div class="flex flex-col gap-4 md:flex-row md:items-center">
        <div
          class="flex flex-shrink-0 items-center gap-3 md:items-center md:overflow-hidden"
          :class="isSubmitted(item) ? 'md:max-w-[70%]' : 'md:max-w-[45%]'"
        >
          <div
            class="rounded-full bg-gray-100 p-2.5"
            aria-hidden="true"
          >
            <ListTodo
              v-if="isQuestionnaireType(item)"
              class="h-5 w-5 text-gray-700 md:h-7 md:w-7"
            />
            <FileText
              v-else
              class="h-5 w-5 text-gray-700 md:h-7 md:w-7"
            />
          </div>

          <div class="flex-1 space-y-2 overflow-hidden">
            <div class="flex items-center justify-between gap-2 md:items-center md:justify-start">
              <h3
                data-cy="ClientPortalItemTitle"
                class="truncate text-sm font-medium leading-5 text-gray-900 md:text-base md:leading-normal"
              >
                {{ getTitle(item) }}
              </h3>
              <Badge
                data-cy="ClientPortalItemBadge"
                :class="getBadgeClass(item)"
                variant="default"
                class="flex-shrink-0 px-1 py-0.5 text-xs md:px-3 md:py-1 md:text-sm"
                role="status"
                :aria-label="t('statusLabel', { status: getBadgeText(item) })"
              >
                <CheckCircle2
                  v-if="isSubmitted(item)"
                  class="mr-1 h-3 w-3 md:h-4 md:w-4"
                  aria-hidden="true"
                />
                <Clock
                  v-else
                  class="mr-1 h-3 w-3 md:h-4 md:w-4"
                  aria-hidden="true"
                />
                {{ getBadgeText(item) }}
              </Badge>
            </div>

            <div
              v-if="getDueDate(item)"
              class="flex items-center gap-1 whitespace-nowrap text-xs text-gray-600 md:text-sm"
            >
              <CalendarIcon
                class="h-4 w-4 md:h-5 md:w-5"
                aria-hidden="true"
              />
              <span>{{ t('dueBy') }} {{ formatDate(getDueDate(item)) }}</span>
            </div>
          </div>
        </div>

        <div
          class="flex flex-col gap-3 md:flex-1 md:flex-row md:items-center md:justify-end md:gap-6"
        >
          <div class="flex items-center gap-2">
            <div
              v-if="getProgressText(item)"
              data-cy="ClientPortalItemProgressText"
              class="flex items-center gap-2 text-xs text-gray-600 md:border-l md:border-gray-200 md:pl-4 md:text-sm"
            >
              <span class="inline md:hidden lg:inline">{{ getProgressText(item, true) }}</span>
              <span class="hidden md:inline lg:hidden">{{ getProgressText(item, false) }}</span>
            </div>

            <div
              v-if="showProgressBar(item)"
              data-cy="ClientPortalItemProgressBar"
              class="min-w-[70px] max-w-[200px] flex-1 md:max-w-[145px]"
            >
              <div
                class="h-2 w-full overflow-hidden rounded-full bg-gray-200 md:h-2.5"
                role="progressbar"
                :aria-valuenow="getProgressPercentage(item)"
                :aria-valuemin="0"
                :aria-valuemax="100"
                :aria-label="getProgressAriaLabel(item)"
              >
                <div
                  class="h-full rounded-full transition-all duration-300"
                  :class="`bg-${themeColor}-600`"
                  :style="{ width: Math.max(getProgressPercentage(item), 5) + '%' }"
                ></div>
              </div>
            </div>
          </div>

          <div class="flex-shrink-0 md:mr-2">
            <component
              :is="buttonType(item)?.component"
              data-cy="ClientPortalItemActionButton"
              v-bind="{
                ...buttonType(item)?.props,
                class: 'w-full min-h-[44px] md:w-auto md:min-h-0',
                accessibleName: getAccessibleButtonName(item)
              }"
              @click="buttonType(item)?.onClick"
            />
          </div>
        </div>
      </div>
    </li>
  </ul>
</template>
